/**
 * Error message sanitizer for production environments
 * Prevents sensitive information from being exposed to users
 */

interface SanitizedError {
  message: string;
  code?: string;
  timestamp: string;
}

/**
 * List of error patterns that should be sanitized in production
 */
const SENSITIVE_PATTERNS = [
  /database/i,
  /sql/i,
  /query/i,
  /connection/i,
  /jwt/i,
  /token/i,
  /secret/i,
  /password/i,
  /api[_\s]key/i,
  /supabase/i,
  /postgres/i,
  /auth\.users/i,
  /node_modules/i,
  /webpack/i,
  /chunk/i,
  /localhost/i,
  /127\.0\.0\.1/i,
  /\b\d{1,3}\.\d{1,3}\.\d{1,3}\.\d{1,3}\b/, // IP addresses
];

/**
 * Generic user-friendly error messages for production
 */
const FRIENDLY_MESSAGES = {
  network: 'Unable to connect to the server. Please check your internet connection.',
  authentication: 'Your session has expired. Please log in again.',
  permission: 'You do not have permission to perform this action.',
  notFound: 'The requested resource was not found.',
  validation: 'Please check your input and try again.',
  server: 'Something went wrong on our end. Please try again later.',
  default: 'An unexpected error occurred. Please try again.',
};

/**
 * Sanitize error message for production display
 */
export function sanitizeErrorMessage(error: Error | string): string {
  const message = typeof error === 'string' ? error : error.message;
  
  // In development, show full error
  if (import.meta.env.DEV) {
    return message;
  }
  
  // Check for sensitive patterns
  const hasSensitiveInfo = SENSITIVE_PATTERNS.some(pattern => 
    pattern.test(message)
  );
  
  if (hasSensitiveInfo) {
    return FRIENDLY_MESSAGES.default;
  }
  
  // Categorize common errors
  const lowerMessage = message.toLowerCase();
  
  if (lowerMessage.includes('network') || lowerMessage.includes('fetch')) {
    return FRIENDLY_MESSAGES.network;
  }
  
  if (lowerMessage.includes('401') || lowerMessage.includes('unauthorized') || 
      lowerMessage.includes('not authenticated')) {
    return FRIENDLY_MESSAGES.authentication;
  }
  
  if (lowerMessage.includes('403') || lowerMessage.includes('forbidden') || 
      lowerMessage.includes('permission')) {
    return FRIENDLY_MESSAGES.permission;
  }
  
  if (lowerMessage.includes('404') || lowerMessage.includes('not found')) {
    return FRIENDLY_MESSAGES.notFound;
  }
  
  if (lowerMessage.includes('validation') || lowerMessage.includes('invalid')) {
    return FRIENDLY_MESSAGES.validation;
  }
  
  if (lowerMessage.includes('500') || lowerMessage.includes('server error')) {
    return FRIENDLY_MESSAGES.server;
  }
  
  // If message looks safe and user-friendly, show it
  if (message.length < 100 && !message.includes('/') && !message.includes('\\')) {
    return message;
  }
  
  return FRIENDLY_MESSAGES.default;
}

/**
 * Sanitize error object for production display
 */
export function sanitizeError(error: Error | string): SanitizedError {
  const message = sanitizeErrorMessage(error);
  
  return {
    message,
    code: import.meta.env.DEV && error instanceof Error ? error.name : undefined,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Sanitize stack trace for production logging
 * Removes absolute paths and sensitive information
 */
export function sanitizeStackTrace(stack?: string): string | undefined {
  if (!stack || import.meta.env.PROD) {
    return undefined;
  }
  
  // In development, return full stack
  if (import.meta.env.DEV) {
    return stack;
  }
  
  // Remove absolute file paths
  return stack
    .split('\n')
    .map(line => line.replace(/\(.*?\/([^/]+)\)/g, '($1)'))
    .join('\n');
}

/**
 * Check if error should be reported to error tracking service
 */
export function shouldReportError(error: Error | string): boolean {
  const message = typeof error === 'string' ? error : error.message;
  
  // Don't report user-triggered errors
  const userErrors = [
    'cancelled',
    'aborted',
    'user denied',
    'popup closed',
  ];
  
  return !userErrors.some(userError => 
    message.toLowerCase().includes(userError)
  );
}
